import { pushLeadEvent, pushBookingEvent } from "@/lib/gtmDataLayer";
import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { Form } from "@/components/ui/form";
import { Sparkles, X } from "lucide-react";
import { AnimatePresence } from "framer-motion";

import StepIndicator from "@/components/consultation/StepIndicator";
import StepDetails from "@/components/consultation/StepDetails";
import StepPricingPlan from "@/components/consultation/StepPricingPlan";
import StepBooking from "@/components/consultation/StepBooking";

const STRICT_EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

const formSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  companyName: z.string().trim().min(1, "Company name is required").max(100),
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .max(255)
    .email("Please enter a valid email address")
    .refine((val) => STRICT_EMAIL_REGEX.test(val), {
      message: "Please enter a complete email address (e.g. name@company.com)",
    }),
  smsReminderNumber: z.string().min(1, "Phone number is required"),
});

type FormValues = z.infer<typeof formSchema>;

interface PlanOption {
  name: string;
  price: string;
}

interface PricingConsultationFormProps {
  onClose: () => void;
  preselectedPlan: string;
  plans: PlanOption[];
}

const PricingConsultationForm = ({ onClose, preselectedPlan, plans }: PricingConsultationFormProps) => {
  const [step, setStep] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState(preselectedPlan);
  const [bookingUrl, setBookingUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      companyName: "",
      email: "",
      smsReminderNumber: "",
    },
  });

  const handleGoToBooking = async () => {
    setIsSubmitting(true);
    const data = form.getValues();

    let geoCity: string | null = null;
    let geoIp: string | null = null;
    try {
      const { data: geo } = await supabase.functions.invoke("geoip-lookup", { method: "GET" });
      geoCity = geo?.city ?? null;
      geoIp = geo?.ip ?? null;
    } catch {
      // ignore
    }

    const submissionId = crypto.randomUUID();
    try {
      await supabase.from("consultation_submissions").insert({
        id: submissionId,
        name: data.name,
        email: data.email,
        company_name: data.companyName,
        phone: data.smsReminderNumber,
        services: [],
        pricing_plan: selectedPlan,
        city: geoCity,
        ip_address: geoIp,
      });
      void pushLeadEvent("pricing_consultation", { name: data.name, email: data.email, phone: data.smsReminderNumber, company: data.companyName, city: geoCity });
    } catch {
      // Don't block booking
    }

    // Trigger email notifications (server-side; caller cannot control content)
    try {
      supabase.functions
        .invoke("notify-consultation-submission", { body: { submissionId } })
        .catch(() => {});
    } catch {
      // Don't block booking
    }


    const url = new URL("https://cal.com/team/smartly/discovery");
    url.searchParams.set("overlayCalendar", "true");
    url.searchParams.set("name", data.name);
    url.searchParams.set("email", data.email);
    url.searchParams.set("smsReminderNumber", data.smsReminderNumber);
    if (data.companyName) url.searchParams.set("companyName", data.companyName);
    url.searchParams.set("embed", "true");

    setBookingUrl(url.toString());
    setIsSubmitting(false);
    setStep(2);
  };

  return (
    <div className="relative rounded-3xl bg-white text-slate-900 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-slate-100 overflow-visible max-w-2xl mx-auto w-full box-border">
      {/* Header */}
      <div className="relative bg-gradient-to-r from-red-600 via-orange-500 to-emerald-500 px-5 py-3 sm:px-8 sm:py-5 md:px-10 md:py-6 rounded-t-3xl overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIxIiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiLz48L3N2Zz4=')] opacity-50" />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-2 sm:p-2.5">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-xl md:text-2xl tracking-tight">
                Get Started with Smartly Managed IT Services
              </h2>
              <p className="text-white/80 text-sm">
                Three simple steps to get started with Smartly Technologies.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-1.5 transition-all duration-200"
            aria-label="Close"
          >
            <X className="h-6 w-6" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      <StepIndicator currentStep={step} variant="pricing" />

      <div className="px-4 sm:px-8 pb-4 sm:pb-8 md:px-10 md:pb-10 overflow-visible">
        <Form {...form}>
          <form onSubmit={(e) => e.preventDefault()}>
            <AnimatePresence mode="wait">
              {step === 0 && (
                <StepDetails key="details" form={form} onNext={() => setStep(1)} />
              )}
              {step === 1 && (
                <StepPricingPlan
                  key="plan"
                  plans={plans}
                  selectedPlan={selectedPlan}
                  onSelectPlan={setSelectedPlan}
                  onBack={() => setStep(0)}
                  onNext={handleGoToBooking}
                  isSubmitting={isSubmitting}
                />
              )}
              {step === 2 && bookingUrl && (
                <StepBooking key="booking" url={bookingUrl} onBack={() => setStep(1)} />
              )}
            </AnimatePresence>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default PricingConsultationForm;
